import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Heart, Minus, Plus, ShieldCheck, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { api, eur } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ChatOrderButtons } from "@/components/ChatOrder";
import Reveal from "@/components/Reveal";

export default function ProductPage() {
  const { slug } = useParams();
  const { t, localize, lang } = useLanguage();
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState({ on: false, x: 50, y: 50 });
  const [variant, setVariant] = useState(60);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setProduct(null);
    setQty(1);
    setVariant(60);
    setActiveImg(0);
    api.productReviews(slug).then(setReviews).catch(() => setReviews([]));
    api
      .product(slug)
      .then((p) => {
        setProduct(p);
        const vs = p.variants || [];
        setVariant(vs.some((v) => v.caps === 60) ? 60 : vs[0]?.caps ?? 60);
        return api.products(p.category);
      })
      .then((list) => setRelated(list.filter((x) => x.slug !== slug).slice(0, 3)))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-32">
        <p data-testid="product-error" className="text-sm text-navy/50">{t.common.error}</p>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-32">
        <p data-testid="product-loading" className="text-sm text-navy/50">{t.common.loading}</p>
      </div>
    );
  }

  const loc = localize(product.i18n);
  const gallery = [product.image, ...(product.images || [])].filter(Boolean);
  const currentPrice = (product.variants || []).find((v) => v.caps === variant)?.price ?? product.price;
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
  const selectedStock = (product.variants || []).find((v) => v.caps === variant)?.stock;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: loc.name,
    description: loc.desc,
    image: product.image,
    brand: { "@type": "Brand", name: "Antalia Pharma" },
    offers: {
      "@type": "Offer",
      price: product.price,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
    },
  };

  return (
    <div data-testid="product-page" className="bg-white pt-24">
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <div className="mx-auto max-w-7xl px-6 pb-24">
        <Link
          to="/shop"
          data-testid="product-back-link"
          className="group inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/50 transition-colors hover:text-gold-dark"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1 rtl:rotate-180 rtl:group-hover:translate-x-1" />
          {t.prod.back}
        </Link>

        <div className="mt-10 grid gap-16 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div
              className={`spotlight-light frame-corners overflow-hidden border border-navy/10 ${product.coming_soon ? "bg-[#0c0c0e]" : "bg-surface"}`}
              data-testid="product-image-frame"
              onMouseEnter={() => setZoom((z) => ({ ...z, on: true }))}
              onMouseLeave={() => setZoom((z) => ({ ...z, on: false }))}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setZoom({ on: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
              }}
            >
              <img
                src={gallery[activeImg] || product.image}
                alt={loc.name}
                data-testid="product-image"
                style={{
                  transformOrigin: `${zoom.x}% ${zoom.y}%`,
                  transform: zoom.on ? "scale(1.7)" : "scale(1)",
                  transition: "transform 0.35s ease-out",
                }}
                className={`aspect-[4/5] w-full cursor-zoom-in ${product.coming_soon ? "object-contain" : "object-cover"}`}
              />
            </div>
            {gallery.length > 1 && (
              <div data-testid="product-gallery" className="mt-4 flex gap-3 overflow-x-auto pb-1">
                {gallery.map((src, i) => (
                  <button
                    key={`${src}-${i}`}
                    type="button"
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    aria-label={`Photo ${i + 1}`}
                    className={`h-16 w-16 shrink-0 overflow-hidden border transition-colors duration-300 ${
                      activeImg === i ? "border-gold" : "border-navy/15 hover:border-gold/60"
                    }`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
            {product.badge && (
              <div className="absolute -top-4 start-6 bg-gold px-4 py-2">
                <p className="label-luxe text-navy">{t.featured.badge}</p>
              </div>
            )}
            {product.coming_soon && (
              <div data-testid="product-coming-soon" className={`absolute -top-4 ${product.badge ? "end-6" : "start-6"} bg-navy px-4 py-2`}>
                <p className="label-luxe text-gold">{t.prod.comingSoon}</p>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="label-luxe text-sage-dark">{t.cats[`${product.category}_n`]}</p>
            <h1 data-testid="product-name" className="mt-4 font-display text-4xl font-light tracking-tight text-navy sm:text-5xl">
              {loc.name}
            </h1>
            <p data-testid="product-tagline" className="mt-3 font-display text-xl italic text-gold-dark">{loc.tagline}</p>
            <p data-testid="product-price" className="mt-6 font-display text-3xl font-medium text-navy">{eur(currentPrice)}</p>
            <p data-testid="product-description" className="mt-6 max-w-lg text-sm font-light leading-relaxed text-navy/60">
              {loc.desc}
            </p>

            <div className="mt-8">
              <p className="label-luxe text-navy/50">{t.fmt.format}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(product.variants || []).map((v) => (
                  <button
                    key={v.caps}
                    data-testid={`variant-${v.caps}`}
                    onClick={() => setVariant(v.caps)}
                    className={`border px-4 py-2.5 text-xs font-semibold transition-colors duration-300 ${
                      variant === v.caps ? "border-navy bg-navy text-white" : "border-navy/15 text-navy/60 hover:border-gold hover:text-gold-dark"
                    }`}
                  >
                    {v.caps} {t.fmt[product.unit] || t.fmt.caps} · {eur(v.price)}
                  </button>
                ))}
              </div>
              {selectedStock !== undefined && selectedStock < 10 && !product.coming_soon && (
                <p data-testid="low-stock-note" className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold-dark">
                  {t.lowStock.replace("{n}", selectedStock)}
                </p>
              )}
              {product.coming_soon && (
                <p data-testid="preorder-note" className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold-dark">
                  {t.preorderNote}
                </p>
              )}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <div className="flex items-center border border-navy/20">
                <button
                  data-testid="product-qty-minus"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="p-3.5 text-navy/60 transition-colors hover:text-gold-dark disabled:opacity-30"
                  disabled={qty <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus size={15} />
                </button>
                <span data-testid="product-qty" className="w-12 text-center text-sm font-semibold">{qty}</span>
                <button
                  data-testid="product-qty-plus"
                  onClick={() => setQty((q) => Math.min(20, q + 1))}
                  className="p-3.5 text-navy/60 transition-colors hover:text-gold-dark"
                  aria-label="Increase quantity"
                >
                  <Plus size={15} />
                </button>
              </div>
              <button
                data-testid="product-add-to-cart"
                onClick={() => addItem(product, qty, variant)}
                className="btn-primary-luxe flex-1 sm:flex-none"
              >
                {t.actions.addToCart}
              </button>
              <button
                data-testid="product-wish-toggle"
                onClick={() => toggle(product.slug)}
                className={`flex h-[50px] w-[50px] items-center justify-center border transition-colors duration-300 ${
                  has(product.slug) ? "border-gold text-gold-dark" : "border-navy/15 text-navy/40 hover:border-gold hover:text-gold-dark"
                }`}
                aria-label="Toggle wishlist"
              >
                <Heart size={17} strokeWidth={1.5} fill={has(product.slug) ? "currentColor" : "none"} />
              </button>
            </div>

            <ChatOrderButtons productName={`${loc.name} · ${variant} ${product.unit || "caps"}`} />

            <div className="mt-12 space-y-8 border-t border-navy/10 pt-10">
              <div>
                <h2 className="label-luxe text-navy">{t.prod.benefits}</h2>
                <ul className="mt-4 space-y-2.5">
                  {loc.benefits.map((b, i) => (
                    <li key={i} data-testid={`product-benefit-${i}`} className="flex items-start gap-3 text-sm font-light text-navy/70">
                      <Check size={15} className="mt-0.5 shrink-0 text-gold-dark" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="label-luxe text-navy">{t.prod.ingredients}</h2>
                <p data-testid="product-ingredients" className="mt-4 text-sm font-light leading-relaxed text-navy/60">
                  {product.ingredients.join(" · ")}
                </p>
              </div>
              <div>
                <h2 className="label-luxe text-navy">{t.prod.usage}</h2>
                <p data-testid="product-usage" className="mt-4 text-sm font-light leading-relaxed text-navy/60">{loc.usage}</p>
              </div>
              <div>
                <h2 className="label-luxe text-navy">{t.prod.certs}</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {product.certifications.map((c, i) => (
                    <span
                      key={i}
                      data-testid={`product-cert-${i}`}
                      className="flex items-center gap-2 border border-sage/50 bg-sage-light/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sage-dark"
                    >
                      <ShieldCheck size={12} />
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {reviews.length > 0 && (
          <div className="mt-20 border-t border-navy/10 pt-14" data-testid="reviews-section">
            <div className="flex flex-wrap items-center gap-4">
              <h2 className="font-display text-3xl font-light tracking-tight text-navy">{t.rev.title}</h2>
              <div className="flex items-center gap-1" data-testid="reviews-average">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} size={15} className={s <= Math.round(avg) ? "fill-gold text-gold" : "text-navy/20"} />
                ))}
                <span className="ms-2 text-xs text-navy/50">{avg.toFixed(1)}</span>
              </div>
            </div>
            <div className="mt-8 grid gap-px bg-navy/10 lg:grid-cols-2">
              {reviews.map((r, i) => {
                const rt = localize(r.i18n);
                return (
                  <div key={i} data-testid={`review-${i}`} className="bg-white p-8">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={13} className={s <= r.rating ? "fill-gold text-gold" : "text-navy/20"} />
                      ))}
                    </div>
                    <p className="mt-4 font-display text-lg font-light italic leading-relaxed text-navy/75">“{rt.text}”</p>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <p className="text-xs font-semibold text-navy">{r.name} — {r.location}</p>
                      {r.verified && (
                        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-sage-dark">
                          <ShieldCheck size={11} />
                          {t.rev.verified}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {related.length > 0 && (
          <div className="mt-28">
            <Reveal>
              <h2 className="font-display text-3xl font-light tracking-tight text-navy">{t.prod.related}</h2>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
